import apiRequest from '@/utils/api'

/**
 * 邮件管理V2 API服务
 * 提供完整的邮件管理功能，包括文件夹管理、历史相关邮件等
 */
class EmailServiceV2 {
  // ================================
  // 🏢 邮件账户管理
  // ================================

  /**
   * 获取邮件账户列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 账户列表响应
   */
  async getEmailAccounts(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/accounts/', { params })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('获取邮件账户列表失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取邮件账户详情
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 账户详情
   */
  async getEmailAccount(accountId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/accounts/${accountId}/`)
      return response.data
    } catch (error) {
      console.error('获取邮件账户详情失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取账户统计信息
   * @param {number} accountId - 账户ID
   * @returns {Promise<Object>} 统计信息
   */
  async getAccountStatistics(accountId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/accounts/${accountId}/statistics/`)
      return response.data
    } catch (error) {
      console.error('获取账户统计失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取账户文件夹列表
   * @param {number} accountId - 账户ID
   * @returns {Promise<Array>} 文件夹列表
   */
  async getAccountFolders(accountId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/accounts/${accountId}/folders/`)
      return response.data
    } catch (error) {
      console.error('获取账户文件夹失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 批量获取所有账户的文件夹列表 - 一次请求返回所有账户的文件夹
   * @returns {Promise<Object>} 账户文件夹字典 {accountId: [folders]}
   */
  async getBulkAccountFolders() {
    try {
      const response = await apiRequest.get('/api/emails/v2/accounts/bulk-folders/')
      return response.data
    } catch (error) {
      console.error('批量获取账户文件夹失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 初始化邮件管理 - 一次性获取所有需要的数据
   * @returns {Promise<Object>} 包含账户、文件夹、统计、标签、分组的完整数据
   */
  async initializeEmailManagement() {
    try {
      const response = await apiRequest.get('/api/emails/v2/accounts/initialize/')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('初始化邮件管理失败:', error)
      return {
        success: false,
        error: this._handleError(error)
      }
    }
  }

  /**
   * 同步邮件账户 - V2 API版本
   * @param {number} accountId - 账户ID
   * @param {string} syncType - 同步类型 ('incremental' 或 'full')
   * @returns {Promise<Object>} 同步结果
   */
  async syncAccount(accountId, syncType = 'incremental') {
    try {
      // 邮件同步可能需要更长时间，设置20分钟超时
      const response = await apiRequest.post(`/api/emails/v2/accounts/${accountId}/sync/`, { 
        sync_type: syncType 
      }, {
        timeout: 1200000  // 20分钟超时，适合大型邮箱或慢网络环境
      })
      return {
        success: true,
        message: response.data.message || '邮件同步已完成',
        data: response.data
      }
    } catch (error) {
      console.error('同步邮件账户失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 批量同步邮件账户（Celery 异步任务）
   * @param {Array<number>} accountIds - 账户ID列表（为空则同步所有）
   * @param {string} syncType - 同步类型 ('incremental' 或 'full')
   * @returns {Promise<Object>} { success: true, task_id: "...", total_accounts: 100 }
   */
  async bulkSyncAccounts(accountIds = [], syncType = 'incremental') {
    try {
      const response = await apiRequest.post('/api/emails/v2/accounts/bulk-sync/', {
        account_ids: accountIds,
        sync_type: syncType
      })
      return response.data
    } catch (error) {
      console.error('启动批量同步任务失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 查询批量同步任务进度
   * @param {string} taskId - 任务ID
   * @returns {Promise<Object>} 进度信息
   */
  async getBulkSyncProgress(taskId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/accounts/bulk-sync-progress/${taskId}/`)
      return response.data
    } catch (error) {
      console.error('查询批量同步进度失败:', error)
      throw this._handleError(error)
    }
  }

  // ================================
  // 📁 文件夹管理（新功能）
  // ================================

  /**
   * 获取文件夹列表
   * @param {Object} params - 查询参数
   * @param {string} params.search - 搜索关键词
   * @param {string} params.folder_type - 文件夹类型
   * @param {number} params.account - 账户ID
   * @returns {Promise<Object>} 文件夹列表响应
   */
  async getFolders(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/folders/', { params })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('获取文件夹列表失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取文件夹详情
   * @param {number} folderId - 文件夹ID
   * @returns {Promise<Object>} 文件夹详情
   */
  async getFolder(folderId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/folders/${folderId}/`)
      return response.data
    } catch (error) {
      console.error('获取文件夹详情失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 创建文件夹
   * @param {Object} folderData - 文件夹数据
   * @param {number} folderData.account - 账户ID
   * @param {string} folderData.name - 文件夹名称
   * @param {string} folderData.folder_type - 文件夹类型
   * @returns {Promise<Object>} 创建的文件夹
   */
  async createFolder(folderData) {
    try {
      const response = await apiRequest.post('/api/emails/v2/folders/', folderData)
      return {
        success: true,
        message: '文件夹创建成功',
        data: response.data
      }
    } catch (error) {
      console.error('创建文件夹失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 更新文件夹
   * @param {number} folderId - 文件夹ID
   * @param {Object} folderData - 更新的文件夹数据
   * @returns {Promise<Object>} 更新的文件夹
   */
  async updateFolder(folderId, folderData) {
    try {
      const response = await apiRequest.put(`/api/emails/v2/folders/${folderId}/`, folderData)
      return {
        success: true,
        message: '文件夹更新成功',
        data: response.data
      }
    } catch (error) {
      console.error('更新文件夹失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 删除文件夹
   * @param {number} folderId - 文件夹ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteFolder(folderId) {
    try {
      await apiRequest.delete(`/api/emails/v2/folders/${folderId}/`)
      return {
        success: true,
        message: '文件夹删除成功'
      }
    } catch (error) {
      console.error('删除文件夹失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取文件夹统计信息
   * @param {number} folderId - 文件夹ID
   * @returns {Promise<Object>} 统计信息
   */
  async getFolderStatistics(folderId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/folders/${folderId}/statistics/`)
      return response.data
    } catch (error) {
      console.error('获取文件夹统计失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取聚合文件夹统计（全部邮件、收件箱、发件箱等）- V2版本
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 聚合统计信息
   */
  async getAggregatedFolderStats(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/folders/aggregated_stats/', { params })
      return response.data
    } catch (error) {
      console.error('获取聚合文件夹统计失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 清空文件夹邮件
   * @param {number} folderId - 文件夹ID
   * @param {boolean} confirm - 是否确认清空重要文件夹
   * @returns {Promise<Object>} 清空结果
   */
  async clearFolderEmails(folderId, confirm = false) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/folders/${folderId}/clear_emails/`, { confirm })
      return response.data
    } catch (error) {
      console.error('清空文件夹邮件失败:', error)
      throw this._handleError(error)
    }
  }

  // ================================
  // ✉️ 邮件管理
  // ================================

  /**
   * 获取邮件列表
   * @param {Object} params - 查询参数
   * @param {string} params.search - 搜索关键词
   * @param {string} params.folder__folder_type - 文件夹类型
   * @param {number} params.account - 账户ID
   * @param {boolean} params.is_read - 是否已读
   * @param {boolean} params.is_important - 是否重要
   * @param {boolean} params.has_attachments - 是否有附件
   * @param {string} params.ordering - 排序方式
   * @returns {Promise<Object>} 邮件列表响应
   */
  async getEmails(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/emails/', { params })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('获取邮件列表失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取邮件详情
   * @param {number} emailId - 邮件ID
   * @returns {Promise<Object>} 邮件详情
   */
  async getEmail(emailId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/emails/${emailId}/`)
      return response.data
    } catch (error) {
      console.error('获取邮件详情失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 按需获取邮件内容
   * @param {number} emailId - 邮件ID
   * @returns {Promise<Object>} 邮件内容获取结果
   */
  async fetchEmailContent(emailId) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/fetch_content/`)
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('按需获取邮件内容失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 高级搜索邮件（新功能）
   * @param {Object} searchData - 搜索条件
   * @param {string} searchData.query - 搜索关键词
   * @param {Array<string>} searchData.folder_types - 文件夹类型列表
   * @param {Array<number>} searchData.account_ids - 账户ID列表
   * @param {string} searchData.sender_email - 发件人邮箱
   * @param {boolean} searchData.has_attachments - 是否有附件
   * @param {boolean} searchData.is_read - 是否已读
   * @param {boolean} searchData.is_important - 是否重要
   * @param {string} searchData.date_from - 开始日期
   * @param {string} searchData.date_to - 结束日期
   * @param {string} searchData.order_by - 排序方式
   * @returns {Promise<Object>} 搜索结果
   */
  async searchEmails(searchData) {
    try {
      const response = await apiRequest.post('/api/emails/v2/emails/search/', searchData)
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('搜索邮件失败:', error)
      throw this._handleError(error)
    }
  }


  /**
   * 获取邮件会话（新功能）
   * @param {number} emailId - 邮件ID
   * @returns {Promise<Object>} 会话邮件列表
   */
  async getEmailThread(emailId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/emails/${emailId}/thread/`)
      return response.data
    } catch (error) {
      console.error('获取邮件会话失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 标记邮件为已读/未读
   * @param {number} emailId - 邮件ID
   * @param {boolean} isRead - 是否已读
   * @returns {Promise<Object>} 操作结果
   */
  async markEmailRead(emailId, isRead = true) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/mark_read/`, { is_read: isRead })
      return response.data
    } catch (error) {
      console.error('标记邮件已读状态失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 批量标记账户邮件为已读
   * @param {number} accountId - 邮件账户ID
   * @param {string} folderType - 可选，文件夹类型过滤
   * @returns {Promise<Object>} 操作结果 {success, updated_count, message}
   */
  async batchMarkAccountRead(accountId, folderType = '') {
    try {
      const data = { account_id: accountId }
      if (folderType) data.folder_type = folderType
      const response = await apiRequest.post('/api/emails/v2/emails/batch-mark-read/', data)
      return response.data
    } catch (error) {
      console.error('批量标记已读失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 标记邮件为重要/不重要
   * @param {number} emailId - 邮件ID
   * @param {boolean} isImportant - 是否重要
   * @returns {Promise<Object>} 操作结果
   */
  async markEmailImportant(emailId, isImportant = true) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/mark_important/`, { is_important: isImportant })
      return response.data
    } catch (error) {
      console.error('标记邮件重要状态失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 标记邮件为收藏/取消收藏（新功能）
   * @param {number} emailId - 邮件ID
   * @param {boolean} isFlagged - 是否收藏
   * @returns {Promise<Object>} 操作结果
   */
  async markEmailFlagged(emailId, isFlagged = true) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/mark_flagged/`, { is_flagged: isFlagged })
      return response.data
    } catch (error) {
      console.error('标记邮件收藏状态失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 移动邮件到收件箱
   * 支持IMAP邮箱和Outlook邮箱，同步到远程服务器
   * @param {number} emailId - 邮件ID
   * @returns {Promise<Object>} 操作结果
   */
  async moveToInbox(emailId) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/move-to-inbox/`)
      return response.data
    } catch (error) {
      console.error('移动邮件到收件箱失败:', error)
      throw this._handleError(error)
    }
  }

  // ================================
  // 📎 附件管理
  // ================================

  /**
   * 获取附件列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 附件列表响应
   */
  async getAttachments(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/attachments/', { params })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('获取附件列表失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取附件详情
   * @param {number} attachmentId - 附件ID
   * @returns {Promise<Object>} 附件详情
   */
  async getAttachment(attachmentId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/attachments/${attachmentId}/`)
      return response.data
    } catch (error) {
      console.error('获取附件详情失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 下载附件
   * @param {number} attachmentId - 附件ID
   * @returns {Promise<Blob>} 附件文件流
   */
  async downloadAttachment(attachmentId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/attachments/${attachmentId}/download/`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      console.error('下载附件失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 下载内联附件
   * @param {string} contentId - 内联附件Content-ID
   * @returns {Promise<Blob>} 内联附件文件流
   */
  async downloadInlineAttachment(contentId) {
    try {
      const encodedContentId = encodeURIComponent(contentId)
      const response = await apiRequest.get(`/api/emails/v2/attachments/inline/${encodedContentId}/`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      console.error('下载内联附件失败:', error)
      throw this._handleError(error)
    }
  }

  // ================================
  // 📊 同步日志管理
  // ================================

  /**
   * 获取同步日志列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 同步日志列表响应
   */
  async getSyncLogs(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/sync-logs/', { params })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('获取同步日志失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取同步日志详情
   * @param {number} logId - 日志ID
   * @returns {Promise<Object>} 同步日志详情
   */
  async getSyncLog(logId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/sync-logs/${logId}/`)
      return response.data
    } catch (error) {
      console.error('获取同步日志详情失败:', error)
      throw this._handleError(error)
    }
  }

  // ================================
  // 📝 回复日志管理
  // ================================

  /**
   * 获取回复日志列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 回复日志列表响应
   */
  async getReplyLogs(params = {}) {
    try {
      const response = await apiRequest.get('/api/emails/v2/reply-logs/', { params })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('获取回复日志失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取回复日志详情
   * @param {number} logId - 日志ID
   * @returns {Promise<Object>} 回复日志详情
   */
  async getReplyLog(logId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/reply-logs/${logId}/`)
      return response.data
    } catch (error) {
      console.error('获取回复日志详情失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取回复邮件统计（新功能）
   * @returns {Promise<Object>} 回复统计信息
   */
  async getReplyStatistics() {
    try {
      const response = await apiRequest.get('/api/emails/v2/reply-logs/statistics/')
      return response.data
    } catch (error) {
      console.error('获取回复统计失败:', error)
      throw this._handleError(error)
    }
  }

  // ================================
  // 🔧 辅助工具方法
  // ================================

  /**
   * 批量操作邮件
   * @param {Object} batchData - 批量操作数据
   * @param {Array<number>} batchData.email_ids - 邮件ID列表
   * @param {string} batchData.action - 操作类型 ('mark_read', 'mark_important', 'mark_flagged', 'delete')
   * @param {*} batchData.value - 操作值
   * @returns {Promise<Object>} 批量操作结果
   */
  async batchOperationEmails(batchData) {
    try {
      const { email_ids, action, value } = batchData
      const promises = email_ids.map(emailId => {
        switch (action) {
          case 'mark_read':
            return this.markEmailRead(emailId, value)
          case 'mark_important':
            return this.markEmailImportant(emailId, value)
          case 'mark_flagged':
            return this.markEmailFlagged(emailId, value)
          default:
            throw new Error(`不支持的批量操作: ${action}`)
        }
      })
      
      const results = await Promise.allSettled(promises)
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      
      return {
        success: true,
        message: `批量操作完成，成功 ${successful} 个，失败 ${failed} 个`,
        successful,
        failed,
        total: email_ids.length
      }
    } catch (error) {
      console.error('批量操作邮件失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 移动邮件到指定文件夹
   * @param {number} emailId - 邮件ID
   * @param {number} folderId - 目标文件夹ID
   * @returns {Promise<Object>} 操作结果
   */
  async moveEmailToFolder(emailId, folderId) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/move_to_folder/`, {
        folder_id: folderId
      })
      return response.data
    } catch (error) {
      console.error('移动邮件到文件夹失败:', error)
      throw this._handleError(error)
    }
  }

  // 获取邮件的归档状态
  async getEmailArchives(emailId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/emails/${emailId}/get_archives/`)
      return response.data
    } catch (error) {
      console.error('获取邮件归档状态失败:', error)
      throw this._handleError(error)
    }
  }

  // 从文件夹取消归档
  async removeFromFolder(emailId, folderId) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/remove_from_folder/`, {
        folder_id: folderId
      })
      return response.data
    } catch (error) {
      console.error('取消归档失败:', error)
      throw this._handleError(error)
    }
  }

  // 获取账户的文件夹及统计信息
  async getFoldersWithStats(accountId) {
    try {
      const response = await apiRequest.get(`/api/emails/v2/accounts/${accountId}/folders/`)
      return response.data
    } catch (error) {
      console.error('获取账户文件夹统计失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 快速搜索邮件
   * @param {string} keyword - 搜索关键词
   * @param {Object} options - 搜索选项
   * @returns {Promise<Object>} 搜索结果
   */
  async quickSearch(keyword, options = {}) {
    const searchData = {
      query: keyword,
      order_by: '-received_date',
      ...options
    }
    return this.searchEmails(searchData)
  }

  /**
   * 获取今日邮件
   * @param {number} accountId - 账户ID（可选）
   * @returns {Promise<Object>} 今日邮件列表
   */
  async getTodayEmails(accountId = null) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const searchData = {
      date_from: today.toISOString(),
      order_by: '-received_date'
    }
    
    if (accountId) {
      searchData.account_ids = [accountId]
    }
    
    return this.searchEmails(searchData)
  }

  /**
   * 获取未读邮件
   * @param {number} accountId - 账户ID（可选）
   * @returns {Promise<Object>} 未读邮件列表
   */
  async getUnreadEmails(accountId = null) {
    const params = {
      is_read: false,
      ordering: '-received_date'
    }
    
    if (accountId) {
      params.account = accountId
    }
    
    return this.getEmails(params)
  }

  /**
   * 获取重要邮件
   * @param {number} accountId - 账户ID（可选）
   * @returns {Promise<Object>} 重要邮件列表
   */
  async getImportantEmails(accountId = null) {
    const params = {
      is_important: true,
      ordering: '-received_date'
    }
    
    if (accountId) {
      params.account = accountId
    }
    
    return this.getEmails(params)
  }

  /**
   * 处理错误
   * @param {Error} error - 错误对象
   * @returns {Object} 格式化的错误对象
   */
  _handleError(error) {
    if (error.response) {
      // HTTP错误响应
      const { status, data } = error.response
      
      // 提取错误信息，支持多种格式
      let errorMessage = '请求失败'
      
      if (data) {
        // 1. 直接的错误信息字段
        if (data.error || data.detail) {
          errorMessage = data.error || data.detail
        }
        // 2. Django REST framework ValidationError 格式（数组）
        else if (Array.isArray(data) && data.length > 0) {
          // 如果是字符串数组
          if (typeof data[0] === 'string') {
            errorMessage = data[0]
          }
          // 如果是对象数组（包含 ErrorDetail）
          else if (data[0] && (data[0].string || data[0].message)) {
            errorMessage = data[0].string || data[0].message
          }
        }
        // 3. 非字段错误（non_field_errors）
        else if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
          errorMessage = data.non_field_errors[0]
        }
        // 4. 字段特定错误（取第一个字段的第一个错误）
        else if (typeof data === 'object' && !Array.isArray(data)) {
          for (const field in data) {
            if (Array.isArray(data[field]) && data[field].length > 0) {
              errorMessage = data[field][0]
              break
            }
          }
        }
      }
      
      return {
        success: false,
        error: errorMessage,
        status,
        code: data?.code || 'HTTP_ERROR',
        details: data
      }
    } else if (error.request) {
      // 网络错误
      return {
        success: false,
        error: '网络连接失败',
        code: 'NETWORK_ERROR'
      }
    } else {
      // 其他错误
      return {
        success: false,
        error: error.message || '未知错误',
        code: 'UNKNOWN_ERROR'
      }
    }
  }

  // ================================
  // 📱 组合方法（为前端组件提供便利）
  // ================================

  /**
   * 发送邮件
   * @param {Object} emailData - 邮件数据
   * @param {number} emailData.from_account_id - 发件账户ID
   * @param {Array} emailData.recipients - 收件人列表 [{email, name}]
   * @param {Array} emailData.cc_recipients - 抄送列表 [{email, name}]
   * @param {string} emailData.subject - 邮件主题
   * @param {string} emailData.content - 邮件内容
   * @param {number} emailData.reply_to_email_id - 回复的邮件ID（可选）
   * @param {Array} emailData.attachments - 附件文件列表（可选）
   * @param {string} emailData.scheduled_at - 计划发送时间 ISO 字符串（可选）
   * @returns {Promise<Object>} 发送结果
   */
  async sendEmail(emailData) {
    try {
      let requestData
      let requestConfig = { timeout: 120000 } // 2分钟超时
      
      // 检查是否有附件，如果有则使用FormData格式
      if (emailData.attachments && emailData.attachments.length > 0) {
        console.log('📎 检测到附件，使用FormData格式发送：', emailData.attachments.map(f => ({ name: f.name, size: f.size, type: f.type })))
        requestData = new FormData()
        
        // 添加基本字段
        requestData.append('from_account_id', emailData.from_account_id)
        requestData.append('recipients', emailData.recipients)
        requestData.append('cc_recipients', emailData.cc_recipients)
        requestData.append('subject', emailData.subject)
        requestData.append('content', emailData.content)
        
        if (emailData.reply_to_email_id) {
          requestData.append('reply_to_email_id', emailData.reply_to_email_id)
        }
        
        if (emailData.scheduled_at) {
          requestData.append('scheduled_at', emailData.scheduled_at)
        }
        
        // 添加附件
        emailData.attachments.forEach((file) => {
          requestData.append('attachments', file)
        })
        
        // 让浏览器自动设置multipart/form-data的Content-Type和boundary
        // 删除默认的application/json Content-Type
        requestConfig.headers = {
          'Content-Type': undefined  // 让浏览器自动设置
        }
      } else {
        // 没有附件时使用JSON格式
        requestData = { ...emailData }
        delete requestData.attachments // 移除空的attachments字段
      }
      
      const response = await apiRequest.post('/api/emails/v2/emails/send/', requestData, requestConfig)
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('发送邮件失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 取消定时发送的邮件
   * @param {number} replyLogId - 回复日志ID
   * @returns {Promise<Object>} 取消结果
   */
  async cancelScheduledEmail(replyLogId) {
    try {
      const response = await apiRequest.post(`/api/emails/v2/reply-logs/${replyLogId}/cancel/`)
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('取消定时邮件失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 更新定时发送的邮件
   * @param {number} replyLogId - 回复日志ID
   * @param {Object} emailData - 邮件数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateScheduledEmail(replyLogId, emailData) {
    try {
      let requestData
      let requestConfig = { timeout: 120000 }

      const hasNewAttachments = emailData.attachments && emailData.attachments.length > 0

      if (hasNewAttachments) {
        requestData = new FormData()
        requestData.append('subject', emailData.subject)
        requestData.append('content', emailData.content)
        requestData.append('recipients', emailData.recipients)
        requestData.append('cc_recipients', emailData.cc_recipients || '[]')
        requestData.append('scheduled_at', emailData.scheduled_at)
        requestData.append(
          'keep_attachment_ids',
          JSON.stringify(emailData.keep_attachment_ids || [])
        )
        emailData.attachments.forEach((file) => {
          requestData.append('attachments', file)
        })
        requestConfig.headers = { 'Content-Type': undefined }
      } else {
        requestData = {
          subject: emailData.subject,
          content: emailData.content,
          recipients: emailData.recipients,
          cc_recipients: emailData.cc_recipients || [],
          scheduled_at: emailData.scheduled_at,
          keep_attachment_ids: emailData.keep_attachment_ids || [],
        }
      }

      const response = await apiRequest.patch(
        `/api/emails/v2/reply-logs/${replyLogId}/update-scheduled/`,
        requestData,
        requestConfig
      )
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('更新定时邮件失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 批量取消定时邮件
   * @param {Array<number>} ids - 任务ID列表
   * @returns {Promise<Object>}
   */
  async batchCancelScheduledEmails(ids) {
    try {
      const response = await apiRequest.post('/api/emails/v2/reply-logs/batch-cancel/', { ids })
      return {
        success: true,
        ...response.data
      }
    } catch (error) {
      console.error('批量取消定时邮件失败:', error)
      throw this._handleError(error)
    }
  }
}

// 导出单例实例
export default new EmailServiceV2()
